import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Box,
  Typography,
  CircularProgress,
  Chip,
} from '@mui/material';
import { WorkflowStatus, BulkStatusUpdateResponse } from '../../../types/workflow';
import { workflowService } from '../../../services/workflowService';

interface BulkStatusUpdateDialogProps {
  open: boolean;
  onClose: () => void;
  selectedBatchIds: number[];
  onUpdateComplete: (result: BulkStatusUpdateResponse) => void;
}

/**
 * BulkStatusUpdateDialog - Dialog for bulk updating workflow status.
 * Allows selecting a target status and optional notes for multiple batches.
 */
export const BulkStatusUpdateDialog: React.FC<BulkStatusUpdateDialogProps> = ({
  open,
  onClose,
  selectedBatchIds,
  onUpdateComplete,
}) => {
  const [statuses, setStatuses] = useState<WorkflowStatus[]>([]);
  const [selectedStatusId, setSelectedStatusId] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch available statuses when dialog opens
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- loading flag is set synchronously before the async fetch; fetch-on-open must react to the parent-owned `open` prop
      setLoadingStatuses(true);
      setError(null);
      workflowService
        .getStatuses()
        .then((data) => {
          setStatuses(data);
          setLoadingStatuses(false);
        })
        .catch((err) => {
          setError(err.message || 'Failed to load statuses');
          setLoadingStatuses(false);
        });
    } else {
      // Reset state when dialog closes
      setSelectedStatusId('');
      setNotes('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (selectedStatusId === '') return;

    setLoading(true);
    setError(null);

    try {
      const result = await workflowService.bulkUpdateStatus({
        workflowIds: selectedBatchIds,
        targetStatusId: selectedStatusId,
        notes: notes || undefined,
      });

      onUpdateComplete(result);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update statuses');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Bulk Status Update
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Update status for {selectedBatchIds.length} batch{selectedBatchIds.length !== 1 ? 'es' : ''}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ width: '100%', mb: 0.5 }}>
              Selected batches:
            </Typography>
            {selectedBatchIds.length <= 10 ? (
              selectedBatchIds.map((id) => (
                <Chip key={id} label={`#${id}`} size="small" variant="outlined" />
              ))
            ) : (
              <Chip label={`${selectedBatchIds.length} batches selected`} size="small" />
            )}
          </Box>

          <FormControl fullWidth disabled={loadingStatuses}>
            <InputLabel id="target-status-label">Target Status</InputLabel>
            <Select
              labelId="target-status-label"
              value={selectedStatusId}
              label="Target Status"
              onChange={(e) => setSelectedStatusId(e.target.value as number)}
            >
              {loadingStatuses ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Loading statuses...
                </MenuItem>
              ) : (
                statuses.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    {status.name}
                    {status.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({status.description})
                      </Typography>
                    )}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <TextField
            label="Notes (optional)"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes for this bulk status change..."
            fullWidth
          />

          <Alert severity="info" variant="outlined">
            This will attempt to transition all selected batches to the chosen status.
            Batches that cannot be transitioned (invalid state) will be skipped.
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || selectedStatusId === '' || loadingStatuses}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? 'Updating...' : `Update ${selectedBatchIds.length} Batch${selectedBatchIds.length !== 1 ? 'es' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkStatusUpdateDialog;
